import React, { useMemo } from 'react';
import intl from 'react-intl-universal';
import { Tag, tailwindOverride, Tooltip } from '@getsynapse/design-system';
import { TaskTemplateDraftDependencies } from '../../types';
import { useTaskBundleDraft } from '../../providers/TaskBundleProvider/TaskBundleProvider';
import TruncatedTableColumn from 'Pages/ProjectsListPage/components/ActiveProjects/components/TruncatedTableColumn/TruncatedTableColumn';

interface TaskTemplatePredecessorsColumnProps {
  dependencies: TaskTemplateDraftDependencies | undefined;
}

const TaskTemplatePredecessorsColumn: React.FC<
  TaskTemplatePredecessorsColumnProps
> = ({ dependencies }) => {
  const { selectors } = useTaskBundleDraft();

  // Compute filtered predecessors list with both id and name upfront
  const predecessors = useMemo(() => {
    if (!dependencies || dependencies.predecessors.length === 0) {
      return [];
    }
    return dependencies.predecessors
      .map((id) => ({
        id,
        name: selectors.getTemplateById(id)?.name,
      }))
      .filter((p): p is { id: string; name: string } => !!p.name);
  }, [dependencies, selectors]);

  // Handle empty list
  if (predecessors.length === 0) {
    return <span>-</span>;
  }

  // Single predecessor: show as truncated pill
  if (predecessors.length === 1) {
    return (
      <TruncatedTableColumn
        content={predecessors[0].name}
        value={predecessors[0].name}
        className={tailwindOverride(
          'bg-neutral-lighter-two rounded-full',
          'font-semibold text-neutral-darker',
          'font-body text-caption-sm lg:text-caption',
          'py-0.5 px-2 max-w-min'
        )}
      />
    );
  }

  // Multiple predecessors: show first name + count badge
  const tooltipContent = (
    <div className='flex flex-col'>
      <div className='px-2 py-1 font-semibold text-sm leading-6 text-neutral-black'>
        {intl.get(
          'SETTINGS_PAGE.TASK_BUNDLE_PAGE.DEPENDENCIES.RELATIONSHIP_DEPENDS_ON'
        )}
      </div>
      <div className='flex flex-col gap-0'>
        {predecessors.map((p) => (
          <div
            key={p.id}
            className={tailwindOverride(
              'border-t border-b border-neutral-lightest-two',
              'py-1 px-2',
              'font-normal text-sm leading-6 text-neutral-black'
            )}
          >
            {p.name}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <Tooltip
      trigger={
        <div className='flex items-center gap-1'>
          <Tag
            label={predecessors[0].name}
            className='bg-neutral-lighter-two max-w-max truncate'
            textClassName='text-neutral-darker font-semibold'
          />
          <Tag
            label={`+ ${predecessors.length - 1}`}
            className='bg-neutral-lighter-two'
            textClassName='text-neutral-darker font-semibold'
          />
        </div>
      }
      openMode='hover2'
      position='topCenter'
      timeout={0}
      showPopper
      className={`w-full truncate`}
      usePortal
      contentProps={{
        className: tailwindOverride(
          'bg-white border border-solid border-neutral-lightest-two',
          'rounded-lg shadow-tooltip',
          'absolute',
          'w-max',
          'z-50',
          'overflow-hidden'
        ),
      }}
    >
      {tooltipContent}
    </Tooltip>
  );
};

export default TaskTemplatePredecessorsColumn;
